import { GoalStatus } from "@pages/Program/utils/enums";
import { useProgram } from "@pages/Program/utils/useProgram";
import Pill from "@shared/components/Pill";
import { GoalItem } from "@shared/interfaces/ProgramFiles";
import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";

interface GoalModalProps {
  title?: string;
  goal: GoalItem;
  onClose: () => void;
}

interface GoalUpdate {
  id: number;
  description: string;
  source: string;
  applied_at: string;
  applied_by?: {
    id: number;
    name: string;
  } | null;
}

type UpdateEntry = GoalUpdate | "No Updates";

const statusColorMap: Record<GoalItem["status"], string> = {
  on_track: "text-[#009947] border-[#009947] bg-[#EAFBF2] border",
  behind: "text-[#F29F04] border-[#F29F04] bg-[#FFF6E6] border",
  at_risk: "text-[#F04438] border-[#F04438] bg-[#FEF3F2] border",
};

const formatDateToLongString = (dateInput: string | Date): string => {
  const date = new Date(dateInput);
  const day = date.getDate();
  const daySuffix =
    day % 10 === 1 && day !== 11
      ? "st"
      : day % 10 === 2 && day !== 12
      ? "nd"
      : day % 10 === 3 && day !== 13
      ? "rd"
      : "th";
  const month = date.toLocaleString("en-US", { month: "long" });
  const year = date.getFullYear();
  return `${month} ${day}${daySuffix}, ${year}`;
};

const formatDateShort = (dateInput: string | Date): string =>
  new Date(dateInput).toLocaleDateString("en-GB", {
    year: "numeric",
    month: "short",
    day: "numeric",
  });

const GoalModal: React.FC<GoalModalProps> = ({ goal, onClose, title }) => {
  const { getProgramUpdatesByType } = useProgram();
  const { programId } = useParams();
  const [updates, setUpdates] = useState<UpdateEntry[]>([]);

  useEffect(() => {
    if (!programId) return;
    (async () => {
      const data = await getProgramUpdatesByType({
        programId,
        type: "goal",
        ids: [goal.id],
      });
      setUpdates(
        data.filter((update: any) => update.target_id === goal.id)[0]
          ?.updates || []
      );
    })();
  }, [programId, goal.id]);

  const hasUpdates =
    updates && updates.length > 0 && updates[0] !== "No Updates";

  return (
    <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-40 z-[99]">
      <div className="bg-white rounded-lg w-full max-w-2xl min-h-[20rem] shadow-lg max-h-[95vh] flex flex-col">
        <div className="flex justify-between items-center border-b px-6 py-4">
          <h2 className="text-lg font-semibold">{title}</h2>
        </div>
        <div className="flex flex-col justify-center flex-1 overflow-y-auto">
          <div className="px-6 pt-4 space-y-4">
            <div key={goal.id} className="border rounded-lg p-4">
              <div className="flex justify-between items-center mb-2">
                <h3 className="font-semibold">{goal.name}</h3>
                <Pill
                  styling={`${statusColorMap[goal.status]}`}
                  text={GoalStatus[goal.status]}
                />
              </div>
              <p className="text-sm mb-2">{goal.description}</p>
              <div className="text-sm flex flex-col space-y-3 mt-4">
                <div className="flex items-center space-x-2">
                  <img src="/milestone.svg" className="w-5" alt="" />
                  <p>
                    Tasks:{" "}
                    {goal.milestones?.length > 0
                      ? goal.milestones.map((m) => m.name).join(", ")
                      : "No tasks linked yet"}
                  </p>
                </div>
                <div className="flex items-center space-x-2">
                  <img src="/projects_black.svg" className="w-5" alt="" />
                  <p>Projects: {goal.projects.map((p) => p.name).join(", ")}</p>
                </div>
                <div className="flex items-center space-x-2">
                  <img src="/calendar_today.svg" className="w-5" alt="" />
                  <p>
                    Dates: {formatDateToLongString(goal.start_date)} –{" "}
                    {formatDateToLongString(goal.end_date)}
                  </p>
                </div>
                <div className="flex items-center space-x-2">
                  <img
                    src="/notifications_active.svg"
                    className="w-5"
                    alt="update icon"
                  />
                  <p>Latest Updates:</p>
                </div>
                <div className="flex flex-col space-y-2 pl-7">
                  {!hasUpdates ? (
                    <p className="text-gray-500 italic text-sm">
                      No recent updates available for this goal.
                    </p>
                  ) : (
                    updates.map((update) => {
                      if (update === "No Updates") return null;
                      const u = update as GoalUpdate;
                      return (
                        <ul key={u.id} className="text-sm list-disc">
                          <li className="text-sm text-gray-500">
                            {u.description}
                          </li>
                        </ul>
                      );
                    })
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
        <div className="flex justify-end px-6 py-4">
          <button
            onClick={onClose}
            className="text-sm text-blue-600 border-blue-600 border rounded-md px-4 py-2 transition duration-200 ease-in-out"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
};

export default GoalModal;
